import { AppState } from '@chegg-wt/core/state'
import React, { useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import {
  origins,
  Region,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { Dispatch } from 'redux'
import { isInvalidQuery } from '@chegg-wt/core/hooks/useSearchSources'
import { defaultSourceType, sourceTypeById } from '@chegg-wt/core/utils/sourceTypes'
import Credibility from '@chegg-wt/core/components/Credibility'
import ErrorContainer from '@chegg-wt/core/components/ErrorContainer'
import MaxWidthContainer from '@chegg-wt/core/components/MaxWidthContainer'
import {
  AppRoute,
  AppRouteName,
  AppRouteOpts,
  SourceType,
  JournalType,
  Source,
} from '@chegg-wt/core/types/core'
import { navigated } from '@chegg-wt/core/state/app/actions'
import {
  sourceTypeSelected,
  sourceQueryEntered,
  sourceSearchResultsPageLoaded,
  sourceSelected,
  journalEngineSelected,
} from '@chegg-wt/core/state/createCitation/actions'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import {
  createCitationSearchResultsSelector,
  createCitationErrorSelector,
  createCitationSearchLoadingSelector,
  createCitationSourceTypeSelector,
  createCitationJournalTypeSelector,
  createCitationQuerySelector,
  currentSourceSelector,
} from '@chegg-wt/core/state/createCitation/selectors'
import { currentStyleSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { updateMessagingFlagSelector } from '@chegg-wt/core/state/app/selectors'
import { CROSSREF_RESULT_COUNT } from '@chegg-wt/core/state/optimizely/constants'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { JOURNAL } from '@chegg-wt/core/constants/journalTypes'
import { Config } from '@chegg-wt/core/config'
import { CM } from '@chegg-wt/core/constants/site'
import CitationSearch from '../../CitationFlow/CitationSearch'
import { useRoutesContext } from '../../RoutesContext'
import { Advert } from '../../Advert'
import { PageHeader, PageHeaderWrapper } from '../styled'
import { SubHeadingBCRest } from '../../BrandConsolidation'
import {
  SearchResultsProps,
  SearchResultsStateProps,
  SearchResultsDispatchProps,
} from './types'
import * as Styled from './styled'

export const SourceSelectionTop: React.FC = () => {
  const updateMessagingFeatureEnabled = useSelector(updateMessagingFlagSelector)
  const { appName } = Config
  return (
    <PageHeaderWrapper>
      <PageHeader>Find your source</PageHeader>
      {(appName !== CM || updateMessagingFeatureEnabled) && <SubHeadingBCRest />}
    </PageHeaderWrapper>
  )
}

const SourceSelection: React.FC<SearchResultsProps> = ({
  query,
  copy,
  currentStyle,
  currentSourceType,
  sourceSearchQuery,
  cite,
  selectSourceType,
  loading,
  error,
  sources,
  journalType,
  setSearchQuery,
  changeJournalType,
  pageLoaded,
  navigated,
  onCreateManualCitationClickCTA,
}) => {
  const routes = useRoutesContext()
  const sourceTypeId = sourceTypeById(currentSourceType) || defaultSourceType
  const searchText = query.searchText || sourceSearchQuery
  const style = query.style || currentStyle
  const source = query.source || currentSourceType
  const [invalidSearch, setInvalidSearch] = useState<boolean>(false)
  const { isEnabled: crossrefResultCountEnabled } = useSafeOptimizelyFeature(
    CROSSREF_RESULT_COUNT
  )

  useEffect(() => {
    if (isInvalidQuery(source, searchText)) {
      setInvalidSearch(true)
      return
    }
    setInvalidSearch(false)
    pageLoaded(searchText, style, source, crossrefResultCountEnabled)
  }, [searchText, style, source, pageLoaded, crossrefResultCountEnabled])

  const onSourceTypeSelect = ({ id: sourceType }: SourceType) => {
    if (sourceType !== source) {
      selectSourceType(sourceType, 'toSourceSelection')
    }
  }

  const onChangeJournalType = (type: JournalType) => {
    changeJournalType(type, crossrefResultCountEnabled)
  }

  const handleSelectSource = (source: Source) => {
    cite(source, sourceSearchQuery)
  }

  const handleSearchQuery = (searchQuery: string) => {
    if (!isInvalidQuery(source, searchQuery)) onChangeJournalType(JOURNAL)
    if (searchQuery !== sourceSearchQuery) {
      setSearchQuery(searchQuery)
    }
  }

  const handleManualCitationClick = (region: Region) => {
    navigated(
      routes.toManualCitation({ style: currentStyle, sourceType: sourceTypeId.value }),
      { replace: true }
    )
    onCreateManualCitationClickCTA(region)
  }

  return (
    <Styled.PageContainer>
      <Styled.ContentWrapper>
        <ErrorContainer />
        <MaxWidthContainer>
          <CitationSearch
            afterFirstSource={<Advert placement="C1" />}
            afterSecondSource={<Advert placement="C2" />}
            isOverflow={false}
            searchText={sourceSearchQuery}
            citationStyle={currentStyle}
            sourceType={sourceTypeId}
            initialSearchQuery={query.searchText}
            loading={loading}
            error={!!error}
            sources={sources}
            journalType={journalType}
            invalidSearch={invalidSearch}
            onChangeSourceType={onSourceTypeSelect}
            onChangeSearchQuery={handleSearchQuery}
            onChangeJournalType={onChangeJournalType}
            onSelectSource={handleSelectSource}
            onManualCiteClick={handleManualCitationClick}
          />
        </MaxWidthContainer>
      </Styled.ContentWrapper>
      {!!copy && <Credibility copy={copy} />}
    </Styled.PageContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  currentStyle: currentStyleSelector(state),
  currentSourceType: createCitationSourceTypeSelector(state),
  sourceSearchQuery: createCitationQuerySelector(state),
  currentSourceTypeResource: currentSourceSelector(state),
  journalType: createCitationJournalTypeSelector(state),
  loading: createCitationSearchLoadingSelector(state),
  sources: createCitationSearchResultsSelector(state),
  error: createCitationErrorSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  pageLoaded: (
    query: string,
    style: string,
    sourceType: string,
    crossrefResultCountEnabled: boolean
  ) =>
    dispatch(
      sourceSearchResultsPageLoaded(query, style, sourceType, crossrefResultCountEnabled)
    ),
  cite: (selectSource: Source, searchQuery: string) =>
    dispatch(sourceSelected(selectSource, searchQuery)),
  selectSourceType: (sourceType: string, redirectTo?: AppRouteName) =>
    dispatch(sourceTypeSelected(sourceType, redirectTo)),
  setSearchQuery: (query: string) => dispatch(sourceQueryEntered(query)),
  changeJournalType: (type: JournalType, crossrefResultCountEnabled: boolean) =>
    dispatch(journalEngineSelected(type, crossrefResultCountEnabled)),
  navigated: (route: AppRoute, opts?: AppRouteOpts) => dispatch(navigated(route, opts)),
  onCreateManualCitationClickCTA: (region: Region) =>
    dispatch(userClick({ origin: origins.createManualCitationCTA, region })),
})

export default connect<SearchResultsStateProps, SearchResultsDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(SourceSelection)
